import type { TFunction } from 'i18next'

import { PickupOrder, PickupOrderStatus } from '@/api/pickup-orders/types/PickupOrder'
import { Pkg } from '@/api/pkg/types/Pkg'
import { PkgShippingMethod, PkgStatus, PkgWarehouse, UserPkg } from '@/api/user-package/types/UserPkg'

import { calcWeight } from './pkgPricing'

export const PKG_UNITE_FEE_PER_PKG = 1.5
export const PKG_SHRINK_FEE = 5
export const NO_TROY_PHONE = '500000000'
export const NO_TROY_EMAIL = 'notroy@shiptanbul.com'

export const getIsPkgReqActive = (pkg: UserPkg) => {
  return pkg.isPhotoRequestActive || pkg.isShrinkRequestActive || pkg.isUniteRequestActive
}
export const getPkgStatus = (pkg: UserPkg, t: TFunction) => {
  if (pkg.isShipped) return t('pkg.status.shipped')
  if (pkg.status === PkgStatus.NeedId) return t('pkg.status.needId')
  if (pkg.isPaid && !pkg.isShipped) return t('pkg.status.waitingShipment')
  if (pkg.isReturnRequestActive) {
    if (pkg.returnLabelFalse) return t('pkg.status.returnLabelRequired')
    return t('pkg.status.waitingReturn')
  }
  if (pkg.isUniteRequestActive) return t('pkg.status.uniting')
  if (getIsPkgReqActive(pkg)) return t('pkg.status.requestPending')
  return t('pkg.status.readyToShip')
}
export const getPickupOrderStatus = (order: PickupOrder, t: TFunction) => {
  if (!order.barcodeImg) return t('pkg.pickupStatus.waitingBarcode')
  if (order.status === PickupOrderStatus.Picked) return t('pkg.pickupStatus.picked')
  if (order.status === PickupOrderStatus.Pending) return t('pkg.pickupStatus.pending')
  if (order.status === PickupOrderStatus.FalseBarcode) return t('pkg.pickupStatus.falseBarcode')
  if (order.status === PickupOrderStatus.Cancelled) return t('pkg.pickupStatus.cancelled')
  return null
}
export const getPkgStatusTooltip = (pkg: UserPkg, t: TFunction) => {
  if (pkg.status === PkgStatus.NeedId) return t('pkg.tooltip.needId')
  if (pkg.isReturnRequestActive) return t('pkg.tooltip.returnActive')
  if (getIsPkgReqActive(pkg)) return t('pkg.tooltip.requestActive')
  return null
}
export const isPkgUniteable = (pkg: UserPkg) => {
  return !pkg.isPaid && !pkg.isShipped && !pkg.isReturnRequestActive && !pkg.isUniteRequestActive
}

/** Shared package shape for add-to-existing-unite UI (client UserPkg + office Pkg). */
export interface UnitePkgSummary {
  id: string
  brand: string
  warehouse: PkgWarehouse
  fromTrackingNumber: string
  toTrackingNumber?: string
  isFlammable: boolean
  weight: number
  height: number
  length: number
  width: number
}

export interface PendingUniteGroup {
  uniteRequestId: string
  packages: UnitePkgSummary[]
  warehouse: PkgWarehouse
  isSplit: boolean
}

export const userPkgToUniteSummary = (pkg: UserPkg): UnitePkgSummary => ({
  id: pkg.id,
  brand: pkg.brand,
  warehouse: pkg.warehouse,
  fromTrackingNumber: pkg.fromTrackingNumber,
  toTrackingNumber: pkg.toTrackingNumber,
  isFlammable: pkg.isFlammable,
  weight: pkg.dimensions.weight,
  height: pkg.dimensions.height,
  length: pkg.dimensions.length,
  width: pkg.dimensions.width,
})

/** Pending unite requests at a warehouse, grouped by uniteRequestId from user packages. */
export const getPendingUniteGroups = (packages: UserPkg[] | undefined, warehouse: PkgWarehouse): PendingUniteGroup[] => {
  if (!packages?.length) return []
  const byRequestId = new Map<string, UnitePkgSummary[]>()
  for (const pkg of packages) {
    if (!pkg.isUniteRequestActive || !pkg.uniteRequestId || pkg.warehouse !== warehouse) continue
    const existing = byRequestId.get(pkg.uniteRequestId) || []
    existing.push(userPkgToUniteSummary(pkg))
    byRequestId.set(pkg.uniteRequestId, existing)
  }
  return Array.from(byRequestId.entries()).map(([uniteRequestId, pkgs]) => ({
    uniteRequestId,
    packages: pkgs,
    warehouse,
    isSplit: pkgs.length > 0 && pkgs.every(p => p.fromTrackingNumber === pkgs[0].fromTrackingNumber),
  }))
}

export const canAddToExistingUnite = (pkg: UserPkg, packages: UserPkg[] | undefined): boolean => {
  return isPkgUniteable(pkg) && getPendingUniteGroups(packages, pkg.warehouse).length > 0
}

export const getUniteRequestShortId = (uniteRequestId: string) => uniteRequestId.replace(/-/g, '').slice(-4).toUpperCase()

export const isUniteGroupTooHeavy = (packages: UnitePkgSummary[]) => {
  const finalWeight = packages.reduce((acc, p) => acc + calcWeight(p.width, p.length, p.height, p.weight), 0)
  return finalWeight >= 20
}

export const hasUniteFlammableMix = (packages: UnitePkgSummary[]) => {
  let isFlammable = false
  let isNormal = false
  for (const p of packages) {
    if (p.isFlammable) isFlammable = true
    else isNormal = true
    if (isFlammable && isNormal) return true
  }
  return false
}

/** Warehouses that have more than one uniteable package (same rule as UnitePkgPage warehouse filter). */
export const getWarehousesWithMultipleUniteablePkgs = (packages: UserPkg[] | undefined): PkgWarehouse[] => {
  if (!packages?.length) return []
  const warehouseToPkgs = packages.filter(isPkgUniteable).reduce(
    (acc, pkg) => {
      acc[pkg.warehouse] = (acc[pkg.warehouse] || 0) + 1
      return acc
    },
    {} as Record<PkgWarehouse, number>
  )
  return (Object.keys(warehouseToPkgs) as PkgWarehouse[]).filter(w => warehouseToPkgs[w] > 1)
}

export const hasPackagesToUnite = (packages: UserPkg[] | undefined): boolean => getWarehousesWithMultipleUniteablePkgs(packages).length > 0
export const getIsSplitPkg = (pkgs: UserPkg[], pkg: UserPkg) => {
  return pkgs.some(p => !p.isShipped && p.id !== pkg.id && p.fromTrackingNumber === pkg.fromTrackingNumber)
}
/** Matches `pkg.productTypes` keys: product type label with spaces removed (see PackageContents). */
export const productCategoryToI18nKey = (category: string) => category.replace(/ /g, '')

export const translateProductCategory = (category: string, t: TFunction) =>
  t(`pkg.productTypes.${productCategoryToI18nKey(category)}`, { defaultValue: category })

export const getShippingMethodLabel = (method: PkgShippingMethod, t: TFunction) => {
  switch (method) {
    case PkgShippingMethod.DoorToDoor:
      return t('pkg.shippingMethod.doorToDoor')
    case PkgShippingMethod.PickUpPoint:
      return t('pkg.shippingMethod.pickUpPoint')
    case PkgShippingMethod.Test:
      return t('pkg.shippingMethod.test')
    default:
      return ''
  }
}

export const DELAY_SUBTRACTED_THRESHOLD_DAYS = 30

function parseDelayStatusDays(delayStatus?: string): number | null {
  if (!delayStatus || !/^\d+$/.test(delayStatus)) return null
  return parseInt(delayStatus, 10)
}

export const getIsPackageSubtracted = (pkg: Pkg) => {
  if (pkg.delayStatus === 'SUBTRACTED' || pkg.delayStatus === 'DONATED') {
    return true
  }
  const days = parseDelayStatusDays(pkg.delayStatus)
  return days !== null && days >= DELAY_SUBTRACTED_THRESHOLD_DAYS
}

export const isDelayStatusAtSubtractedThreshold = (delayStatus?: string): boolean => {
  if (delayStatus === 'SUBTRACTED' || delayStatus === 'DONATED') {
    return true
  }
  const days = parseDelayStatusDays(delayStatus)
  return days !== null && days >= DELAY_SUBTRACTED_THRESHOLD_DAYS
}
export const getIsShrinkAllowed = (pkg: Pkg | UserPkg) => {
  const isTextile = (pkgCat: string) => ['Underwear', 'Pants', 'Shirt', 'Skirt', 'Jackets', 'Home Textile'].includes(pkgCat)
  const areAllTextile = (contents: typeof pkg.contents) => contents.every(c => isTextile(c.productCategory))
  //textile items cannot be shrunk in UAE warehouse
  const allowedWarehouses = [PkgWarehouse.USA, PkgWarehouse.UK]

  const allowShrinkUae = pkg.warehouse === PkgWarehouse.UAE && !areAllTextile(pkg.contents) // Returns false if all are textile
  const isMaxShrinked = pkg.isShrinked !== undefined && pkg.isShrinked === true
  const allowShrink = pkg.isShrinked === false || allowedWarehouses.includes(pkg.warehouse) || allowShrinkUae
  return !isMaxShrinked && allowShrink
}
